import { useState } from 'react';
import Theme from '../styles/Theme.js';
import Utils from '../Utils.js';

/*
 * Field
 *
 * The form controls, and the rules for what counts as filled in.
 *
 * Validation runs on SUBMIT and then on every change, never on first blur. A
 * message that appears the moment someone tabs out of a half-typed email is
 * telling them they got it wrong before they had finished; once they have
 * pressed Continue, live correction is exactly what they want.
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const YEAR_FLOOR = 1970;

// The rule per kind. Returns an error string, or null when the value is fine.
const RULES = {
  email: value => Utils.validateEmail(value) ? null : 'That does not look like an email address.',
  tel: value => {
    let digits = String(value).replace(/[^0-9]/g, '');
    return digits.length === 10 || digits.length === 11 && digits.startsWith('1') ? null : 'Please enter a 10-digit phone number.';
  },
  year: value => {
    let year = parseInt(value, 10);
    let latest = new Date().getFullYear();
    if (!(year >= YEAR_FLOOR && year <= latest)) {
      return `Please enter a year between ${YEAR_FLOOR} and ${latest}.`;
    }
    return null;
  }
};

/*
 * Validate one field. Empty is only an error when the field is required, which
 * is what lets the message step be genuinely optional.
 */
export const validateField = (field, value) => {
  let text = String(value ?? '').trim();
  if (text.length === 0) {
    return field.required ? `${field.label} is required.` : null;
  }
  return RULES[field.kind] ? RULES[field.kind](text) : null;
};

// Validate a whole node's worth of fields, returning { fieldId: message }.
export const validateFields = (fields, values) => {
  return (fields || []).reduce((errors, field) => {
    let message = validateField(field, values[field.id]);
    return message ? {
      ...errors,
      [field.id]: message
    } : errors;
  }, {});
};
const Field = ({
  error,
  field,
  onChange,
  value
}) => {
  const [focused, setFocused] = useState(false);
  const multiline = field.kind === 'textarea';

  // Phone numbers are formatted as they are typed, but the raw digits are
  // what travel, the payload strips punctuation again on the way out.
  const onValueChange = next => {
    onChange(field.kind === 'tel' ? Utils.formatPhoneNumber(next) : next);
  };
  const borderColor = () => {
    if (error) {
      return Theme.colors.error;
    }
    return focused ? Theme.colors.primary() : Theme.colors.border();
  };
  const controlStyle = {
    ...Theme.styles.input(),
    borderColor: borderColor(),
    borderRadius: multiline ? 14 : 12,
    boxShadow: focused ? `0 0 0 3px ${Theme.tint(0.14)}` : 'none',
    minHeight: multiline ? 120 : undefined,
    padding: '13px 16px',
    resize: multiline ? 'vertical' : undefined
  };
  const shared = {
    'aria-describedby': error ? `${field.id}-error` : undefined,
    'aria-invalid': error ? true : undefined,
    autoComplete: field.autoComplete,
    id: field.id,
    onBlur: () => setFocused(false),
    onChange: event => onValueChange(event.target.value),
    onFocus: () => setFocused(true),
    placeholder: field.placeholder,
    style: controlStyle,
    value: value ?? ''
  };
  return /*#__PURE__*/_jsxs("div", {
    style: {
      marginBottom: 18,
      width: '100%'
    },
    children: [/*#__PURE__*/_jsxs("label", {
      htmlFor: field.id,
      style: {
        ...Theme.textStyles.caption({
          color: Theme.colors.text(),
          fontWeight: 600
        }),
        display: 'block',
        marginBottom: 7
      },
      children: [field.label, field.required !== true && /*#__PURE__*/_jsx("span", {
        style: {
          color: Theme.colors.lightGrey,
          fontWeight: 400
        },
        children: ' (optional)'
      })]
    }), multiline ? /*#__PURE__*/_jsx("textarea", {
      ...shared,
      rows: 4
    }) : field.kind === 'select' ? /*#__PURE__*/_jsxs("select", {
      ...shared,
      style: {
        ...controlStyle,
        cursor: 'pointer'
      },
      children: [/*#__PURE__*/_jsx("option", {
        value: '',
        children: field.placeholder || 'Choose one'
      }), (field.options || []).map(option => /*#__PURE__*/_jsx("option", {
        value: option.value,
        children: option.label
      }, option.value))]
    }) : /*#__PURE__*/_jsx("input", {
      ...shared,
      inputMode: field.kind === 'year' ? 'numeric' : undefined,
      maxLength: field.kind === 'year' ? 4 : undefined,
      type: field.kind === 'year' ? 'text' : field.kind || 'text'
    }), field.help && !error && /*#__PURE__*/_jsx("span", {
      style: {
        ...Theme.textStyles.caption(),
        display: 'block',
        marginTop: 6
      },
      children: field.help
    }), error && /*#__PURE__*/_jsx("span", {
      id: `${field.id}-error`,
      role: 'alert',
      style: {
        ...Theme.textStyles.caption({
          color: Theme.colors.error
        }),
        display: 'block',
        marginTop: 6
      },
      children: error
    })]
  });
};
export default Field;